import RecordCenter from './records/RecordCenter';
import React, { useState, useEffect, useRef } from 'react';
import { Activity, HeartPulse, TrendingUp, Camera, Salad, Droplet, ClipboardList, Dumbbell, Smile, Code, Sparkles } from 'lucide-react';
import { diariesApi, peopleApi, nutritionApi } from '../api';
import WaterDrinkModal from './WaterDrinkModal';
import HealthRecordDialog, { type VitalRecordKind } from './HealthRecordDialog';
import VitalHistoryDialog from './VitalHistoryDialog';
import { AiSnapResultSheet } from './aiSnap/AiSnapResultSheet';
import { AiSnapWikiSheet } from './aiSnap/AiSnapWikiSheet';
import { AiSnapScanningOverlay, type AiSnapPhase } from './aiSnap/AiSnapScanningOverlay';
import { prepareAiSnapImage } from './aiSnap/prepareAiSnapImage';
import { useAppBackHandler } from '../navigation/AppNavigation';
import { toLocalDateInputValue } from '../dateUtils';

interface Diary {
  id: number;
  person_id: number;
  content: string;
  date: string;
  created_at: string;
  diary_type?: DiaryType;
  person_name?: string;
  person_avatar?: string;
  person_color?: string;
  images?: DiaryImage[];
}

interface DiaryImage {
  id: number;
  url: string;
  created_at?: string;
}

export type DiaryType = 'grudge' | 'gratitude' | 'note' | 'mood' | 'fitness' | 'tech';

// Special compose intent to open the record category picker (used by App bottom center + button)
export type DiaryComposeIntent = DiaryType | 'categories' | 'public-note';

/* 标签配置 */
const TAG_OPTIONS: { key: DiaryType; label: string; icon: string; color: string; bgColor: string }[] = [
  { key: 'note',      label: '日常', icon: '📋', color: '#0984E3', bgColor: '#E8F4FD' },
  { key: 'mood',      label: '情绪', icon: '💭', color: '#A29BFE', bgColor: '#F3F0FF' },
  { key: 'fitness',   label: '健身', icon: '💪', color: '#10B981', bgColor: '#ECFDF5' },
  { key: 'tech',      label: '技术', icon: '💻', color: '#F59E0B', bgColor: '#FFFBEB' },
];

// 保留记仇/记恩的映射用于显示旧数据
const LEGACY_TAG_MAP: Record<string, { label: string; icon: string; color: string; bgColor: string }> = {
  grudge:    { label: '记仇', icon: '😤', color: '#FF6B6B', bgColor: '#FFF0F0' },
  gratitude: { label: '记恩', icon: '🙏', color: '#00B894', bgColor: '#E8FFF8' },
};

const TAG_MAP: Record<string, { label: string; icon: string; color: string; bgColor: string }> = {
  ...Object.fromEntries(TAG_OPTIONS.map(t => [t.key, t])),
  ...LEGACY_TAG_MAP,
};

// 快捷心情（用于弹窗顶部的 5 个按钮，样式参考 frontend/tmp/main.html）
const QUICK_MOODS: { emoji: string; label: string; color: string }[] = [
  { emoji: '😡', label: '愤怒', color: '#EF4444' },
  { emoji: '😢', label: '低落', color: '#60A5FA' },
  { emoji: '🤔', label: '思考', color: '#A78BFA' },
  { emoji: '😌', label: '平静', color: '#34D399' },
  { emoji: '🥳', label: '兴奋', color: '#FBBF24' },
];

const MOOD_EMOJIS = [
  { emoji: '😊', label: '开心' }, { emoji: '😄', label: '大笑' },
  { emoji: '🥰', label: '幸福' }, { emoji: '😌', label: '平静' },
  { emoji: '😎', label: '自信' }, { emoji: '🤗', label: '温暖' },
  { emoji: '😢', label: '难过' }, { emoji: '😭', label: '大哭' },
  { emoji: '😤', label: '生气' }, { emoji: '😡', label: '愤怒' },
  { emoji: '😰', label: '焦虑' }, { emoji: '😱', label: '害怕' },
  { emoji: '😔', label: '失落' }, { emoji: '😩', label: '疲惫' },
  { emoji: '🤔', label: '思考' }, { emoji: '😴', label: '困倦' },
  { emoji: '🤒', label: '不适' }, { emoji: '🥳', label: '庆祝' },
  { emoji: '😍', label: '喜爱' }, { emoji: '🙄', label: '无语' },
];

const toLocalDatetimeStr = (d: Date) => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface Person {
  id: number;
  name: string;
  avatar?: string;
  color?: string;
}

interface DiaryFeedProps {
  onPersonClick?: (personId: number) => void;
  onDiarySaved?: () => void;
  /** 选择「饮食」后跳转到营养管理页（饮水使用弹窗，不再跳转） */
  onNavigateNutrition?: (mode: 'meal' | 'water' | 'review', date?: string) => void;
  onNavigateExercise?: () => void;
  /** 从其他维度页跳转：打开撰写并预选日记类型 */
  composeIntent?: DiaryComposeIntent | null;
  onComposeIntentHandled?: () => void;
  initialFilterType?: DiaryType | 'all';
  initialRecordKey?: string;
}

const DiaryFeed: React.FC<DiaryFeedProps> = ({
  onPersonClick, onDiarySaved, onNavigateNutrition, onNavigateExercise, composeIntent, onComposeIntentHandled, initialFilterType = 'all', initialRecordKey,
}) => {
  const [people, setPeople] = useState<Person[]>([]);

  const [showEditor, setShowEditor] = useState(false);
  /** 新建：先选类别；编辑：直接进入撰写 */
  const [recordPhase, setRecordPhase] = useState<'categories' | 'compose'>('categories');
  const [editingDiary, setEditingDiary] = useState<Diary | null>(null);
  const [content, setContent] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [diaryType, setDiaryType] = useState<DiaryType>('note');
  const [selectedMood, setSelectedMood] = useState<string>('');
  const [isPublic, setIsPublic] = useState(false);
  const [showMoodPicker, setShowMoodPicker] = useState(false);
  const [showWaterModal, setShowWaterModal] = useState(false);
  const [quickHealthKind, setQuickHealthKind] = useState<VitalRecordKind | null>(null);
  const [vitalHistoryKind, setVitalHistoryKind] = useState<VitalRecordKind | null>(null);
  const [healthToast, setHealthToast] = useState<{ message: string; kind: VitalRecordKind } | null>(null);
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const aiSnapInputRef = useRef<HTMLInputElement>(null);
  const [aiSnapping, setAiSnapping] = useState(false);
  const [aiSnapScanUrl, setAiSnapScanUrl] = useState<string | null>(null);
  const [aiSnapPhase, setAiSnapPhase] = useState<AiSnapPhase>('preparing');
  const [aiSnapElapsedSeconds, setAiSnapElapsedSeconds] = useState(0);
  const [aiSnapModalOpen, setAiSnapModalOpen] = useState(false);
  const [aiSnapPreviewUrl, setAiSnapPreviewUrl] = useState<string | null>(null);
  const [aiSnapAnalysis, setAiSnapAnalysis] = useState<any>(null);
  const [, setAiSnapSaveBusy] = useState(false);
  const [aiSnapSuggestions, setAiSnapSuggestions] = useState<any>(null);
  const [aiSnapSuggestionsLoading, setAiSnapSuggestionsLoading] = useState(false);
  const [aiSnapWikiOpen, setAiSnapWikiOpen] = useState(false);
  const [aiSnapWikiTitle, setAiSnapWikiTitle] = useState('');
  const [aiSnapWikiInfo, setAiSnapWikiInfo] = useState<any>(null);
  const [aiSnapWikiLoading, setAiSnapWikiLoading] = useState(false);
  const [aiSnapMealType, setAiSnapMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>(() => {
    const h = new Date().getHours();
    if (h < 10) return 'breakfast';
    if (h < 14) return 'lunch';
    if (h < 18) return 'snack';
    return 'dinner';
  });
  // images
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const closePreview = () => {
    if (previewImageUrl && previewImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setPreviewImageUrl(null);
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!healthToast) return;
    const timer = window.setTimeout(() => setHealthToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [healthToast]);

  useEffect(() => {
    if (!aiSnapping) {
      setAiSnapElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setAiSnapElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [aiSnapping]);

  useEffect(() => {
    if (!composeIntent) return;
    if (composeIntent === 'public-note') {
      openEditor();
      setDiaryType('note');
      setRecordPhase('compose');
      setIsPublic(true);
      onComposeIntentHandled?.();
      return;
    }
    if (composeIntent === 'categories') {
      // Open the same category picker as the old floating pen FAB.
      openEditor();
      onComposeIntentHandled?.();
      return;
    }
    setEditingDiary(null);
    setDiaryType(composeIntent);
    setContent('');
    setSelectedMood('');
    setRecordPhase('compose');
    setShowEditor(true);
    if (composeIntent === 'mood') setShowMoodPicker(true);
    onComposeIntentHandled?.();
  }, [composeIntent, onComposeIntentHandled]);

  const loadData = async () => {
    try { const response = await peopleApi.getAll(); setPeople(Array.isArray(response.data) ? response.data : []); }
    catch (error) { console.warn('关联人物加载失败:', error); }
  };

  /* ===== Editor helpers ===== */
  const closeRecordModal = () => {
    setShowEditor(false);
    setRecordPhase('categories');
    setEditingDiary(null);
  };

  const openEditor = () => {
    setEditingDiary(null);
    setContent('');
    setSelectedPersonId(null);
    setDiaryType('note');
    setSelectedMood('');
    setIsPublic(false);
    setShowMoodPicker(false);
    setRecordPhase('categories');
    const now = new Date();
    setDate(toLocalDatetimeStr(now));
    setSelectedFiles([]);
    setShowEditor(true);
  };


  const handleMoodSelect = (emoji: string, label: string) => {
    setSelectedMood(emoji);
    if (!content.trim()) {
      setContent(`${emoji} 今天感觉${label}`);
    }
    setShowMoodPicker(false);
  };

  /* ===== File handling ===== */
  const addFiles = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const list = Array.isArray(files) ? files : Array.from(files);
    setSelectedFiles(prev => {
      const merged = [...prev, ...list];
      const uniq: File[] = [];
      const seen = new Set<string>();
      for (const f of merged) {
        const key = `${f.name}_${f.size}_${f.lastModified}`;
        if (!seen.has(key)) { seen.add(key); uniq.push(f); }
      }
      if (uniq.length > 9) { alert('最多只能选择 9 张图片'); return uniq.slice(0, 9); }
      return uniq;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const triggerGalleryInput = () => { if (selectedFiles.length < 9 && !saving) galleryInputRef.current?.click(); };
  const removeSelectedFile = (index: number) => { setSelectedFiles(prev => prev.filter((_, i) => i !== index)); };

  const handleAiSnapChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    // 先展示原图，让 Android 拍照返回后立即获得可见反馈；上传仍使用优化后的图片。
    let localUrl: string | null = URL.createObjectURL(file);
    setAiSnapPhase('preparing');
    setAiSnapElapsedSeconds(0);
    setAiSnapScanUrl(localUrl);
    setAiSnapping(true);
    try {
      const preparedFile = await prepareAiSnapImage(file);
      setAiSnapPhase('recognizing');
      const res = await nutritionApi.quickAiSnapAnalyze(preparedFile);
      const analysis = res.data?.analysis;

      // 服务端不再回传图片 Base64；直接复用本地预览，减少响应体积和内存占用。
      setAiSnapPreviewUrl(localUrl);
      setAiSnapAnalysis(analysis || null);
      setAiSnapModalOpen(true);
      if (analysis) {
        setAiSnapSuggestionsLoading(true);
        nutritionApi.getAiSnapSuggestions(analysis)
          .then(res => setAiSnapSuggestions(res.data || null))
          .catch(() => setAiSnapSuggestions(null))
          .finally(() => setAiSnapSuggestionsLoading(false));
      }
    } catch (err: any) {
      if (localUrl) {
        try { URL.revokeObjectURL(localUrl); } catch { /* ignore */ }
      }
      const code = err.response?.data?.code;
      const msg = err.response?.data?.detail || err.message;
      const requestId = err.response?.data?.request_id;
      const suffix = requestId ? `\n请求编号：${requestId}` : '';
      alert(`${code === 'AI_TIMEOUT' ? 'AI 识别超时' : 'AI 识别失败'}: ${msg}${suffix}`);
    } finally {
      setAiSnapping(false);
      setAiSnapScanUrl(null);
    }
  };

  const closeAiSnapModal = () => {
    setAiSnapModalOpen(false);
    setAiSnapAnalysis(null);
    setAiSnapSuggestions(null);
    setAiSnapSuggestionsLoading(false);
    setAiSnapWikiOpen(false);
    setAiSnapWikiTitle('');
    setAiSnapWikiInfo(null);
    setAiSnapWikiLoading(false);
    // previewUrl 可能是 objectURL（兜底情况），关闭时尝试释放
    if (aiSnapPreviewUrl && aiSnapPreviewUrl.startsWith('blob:')) {
      try { URL.revokeObjectURL(aiSnapPreviewUrl); } catch { /* ignore */ }
    }
    setAiSnapPreviewUrl(null);
    setAiSnapSaveBusy(false);
  };

  useAppBackHandler(() => {
    // 识别请求无法安全中止；拦截 Android 返回键，避免用户误以为任务已取消。
    if (aiSnapping) return true;
    if (vitalHistoryKind) {
      setVitalHistoryKind(null);
      return true;
    }
    if (quickHealthKind) {
      setQuickHealthKind(null);
      return true;
    }
    if (previewImageUrl) {
      closePreview();
      return true;
    }
    if (aiSnapWikiOpen) {
      setAiSnapWikiOpen(false);
      return true;
    }
    if (aiSnapModalOpen) {
      closeAiSnapModal();
      return true;
    }
    if (showWaterModal) {
      setShowWaterModal(false);
      return true;
    }
    if (showEditor) {
      const dirty = recordPhase === 'compose' && (Boolean(content.trim()) || selectedFiles.length > 0);
      if (dirty && !window.confirm('当前记录尚未保存，确定放弃吗？')) return true;
      closeRecordModal();
      return true;
    }
    return false;
  });

  /* ===== Save ===== */
  const handleSave = async () => {
    if (!content.trim()) { alert('请输入内容'); return; }
    setSaving(true);
    try {
      const dateValue = date ? new Date(date).toISOString() : undefined;
      if (editingDiary) {
        await diariesApi.update(editingDiary.id, content, dateValue);
        if (selectedFiles.length > 0) await diariesApi.uploadImages(editingDiary.id, selectedFiles);
      } else {
        const requestData: any = { content, diary_type: diaryType, mood: selectedMood, is_public: isPublic };
        if (dateValue) requestData.date = dateValue;
        if (selectedPersonId) requestData.person_id = selectedPersonId;
        const created = await diariesApi.create(requestData);
        const diaryId = created?.data?.id;
        if (diaryId && selectedFiles.length > 0) await diariesApi.uploadImages(diaryId, selectedFiles);
      }
      closeRecordModal();
      setContent('');
      setSelectedPersonId(null);
      setSelectedFiles([]);
      loadData();
      onDiarySaved?.();
    } catch (error: any) { alert('保存失败: ' + (error.response?.data?.detail || error.message)); }
    finally { setSaving(false); }
  };


  /* ===== Mention ===== */
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const pos = e.target.selectionStart;
    setContent(value);
    setCursorPosition(pos);
    const textBeforeCursor = value.slice(0, pos);
    const atMatch = textBeforeCursor.match(/@(\S*)$/);
    if (atMatch) { setMentionFilter(atMatch[1].toLowerCase()); setShowMentionList(true); }
    else { setShowMentionList(false); }
  };

  const handleMentionSelect = (person: Person) => {
    const textBeforeCursor = content.slice(0, cursorPosition);
    const textAfterCursor = content.slice(cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    setContent(textBeforeCursor.slice(0, atIndex) + `@${person.name} ` + textAfterCursor);
    setSelectedPersonId(person.id);
    setShowMentionList(false);
    setTimeout(() => { textareaRef.current?.focus(); }, 0);
  };

  const insertTemplate = (template: string) => {
    setContent(prev => {
      const newContent = prev ? prev + '\n' + template : template;
      return newContent;
    });
    setTimeout(() => {
      textareaRef.current?.focus();
      const length = textareaRef.current?.value.length || 0;
      textareaRef.current?.setSelectionRange(length, length);
    }, 0);
  };

  const filteredPeople = people.filter(p => p.name.toLowerCase().includes(mentionFilter));
  const getSelectedPerson = () => people.find(p => p.id === selectedPersonId);

  const getPlaceholder = (type: DiaryType) => {
    switch (type) {
      case 'note':
        return '发生了什么有趣的事？记录下生活的点滴... #日常\n\n💡 输入 @ 可以关联人员';
      case 'mood':
        return '此刻感受如何？为什么会有这样的情绪... #情绪\n\n💡 输入 @ 可以关联人员';
      case 'fitness':
        return '记录今天的训练情况：\n例如：\n深蹲 50kg 8次x4组\n卧推 40kg 10次x4组\n\n身体状态如何？ #健身\n\n💡 输入 @ 可以关联人员';
      case 'tech':
        return '今天学到了什么新知识？\n记录技术笔记、代码片段或解决问题的思路... #技术\n\n💡 输入 @ 可以关联人员';
      default:
        return '发生了什么？此刻的精神状态如何... #标签\n\n💡 输入 @ 可以关联人员';
    }
  };

  return (
    <div className="diary-feed-root desktop-diary-root">
    <div className="diary-feed">

      <RecordCenter initialRecordKey={initialRecordKey} initialNoteType={initialFilterType} onCompose={openEditor} onChanged={onDiarySaved} onPerson={onPersonClick} onOpenExercise={onNavigateExercise}/>

      {/* ===== 记录弹窗：先选类别，再撰写；饮食跳转营养页；饮水打开专用弹窗 ===== */}
      {showEditor && (
        <div className="record-modal" onClick={(e) => e.target === e.currentTarget && closeRecordModal()}>
          <div className="record-sheet" role="dialog" aria-modal="true" aria-labelledby="quick-record-title">
            <div className="record-head">
              <div className="record-head-side">
                {recordPhase === 'compose' && !editingDiary && (
                  <button type="button" className="record-back" onClick={() => setRecordPhase('categories')} aria-label="返回">←</button>
                )}
              </div>
              <h2 className="record-title" id="quick-record-title">
                {editingDiary ? '编辑记录' : recordPhase === 'categories' ? '选择记录类型' : `记录·${TAG_MAP[diaryType]?.label || '日常'}`}
              </h2>
              <button className="record-close" onClick={closeRecordModal} aria-label="关闭">✕</button>
            </div>

            {recordPhase === 'categories' && !editingDiary && (
              <div className="qn-bento">
                {/* AI 快拍 */}
                  <label className="qn-bento-item qn-bento-ai">
                  <input type="file" accept="image/*" capture="environment" ref={aiSnapInputRef} style={{ display: 'none' }} onChange={handleAiSnapChange} />
                  <div className="icon"><Camera size={30} strokeWidth={2.2} /></div>
                  <div className="info">
                    <strong>AI 快拍识别</strong>
                    <span>拍照识别食物/饮料/酒水，营养+百科</span>
                  </div>
                  <Sparkles className="deco" size={78} aria-hidden="true" />
                </label>

                <button type="button" className="qn-bento-item qn-bento-health qn-bento-bp" onClick={() => { closeRecordModal(); setQuickHealthKind('bp'); }}>
                  <span className="qn-health-icon"><HeartPulse size={22} /></span>
                  <span className="qn-health-info"><strong>血压</strong><small>快速录入 · 趋势</small></span>
                  <TrendingUp className="qn-health-trend" size={16} aria-hidden="true" />
                </button>

                <button type="button" className="qn-bento-item qn-bento-health qn-bento-hr" onClick={() => { closeRecordModal(); setQuickHealthKind('hr'); }}>
                  <span className="qn-health-icon"><Activity size={22} /></span>
                  <span className="qn-health-info"><strong>心率</strong><small>快速录入 · 趋势</small></span>
                  <TrendingUp className="qn-health-trend" size={16} aria-hidden="true" />
                </button>

                {/* 饮食 */}
                <button type="button" className="qn-bento-item qn-bento-meal" onClick={() => { onNavigateNutrition?.('meal'); closeRecordModal(); }}>
                  <div className="icon"><Salad size={26} strokeWidth={2.2} /></div>
                  <div className="info">
                    <strong>饮食</strong>
                    <span>记录餐食热量</span>
                  </div>
                </button>

                {/* 饮水 */}
                <button type="button" className="qn-bento-item qn-bento-water" onClick={() => { setShowWaterModal(true); closeRecordModal(); }}>
                  <div className="icon"><Droplet size={22} strokeWidth={2.2} /></div>
                  <strong>饮水</strong>
                </button>

                {/* 日常 */}
                <button type="button" className="qn-bento-item qn-bento-note" onClick={() => { setDiaryType('note'); setRecordPhase('compose'); }}>
                  <div className="icon"><ClipboardList size={22} strokeWidth={2.2} /></div>
                  <strong>日常</strong>
                </button>

                {/* 健身 */}
                <button type="button" className="qn-bento-item qn-bento-fitness" onClick={() => { setDiaryType('fitness'); setRecordPhase('compose'); }}>
                  <div className="icon"><Dumbbell size={26} strokeWidth={2.2} /></div>
                  <div className="info">
                    <strong>健身</strong>
                    <span>训练与消耗</span>
                  </div>
                </button>

                {/* 情绪 */}
                <button type="button" className="qn-bento-item qn-bento-mood" onClick={() => { setDiaryType('mood'); setRecordPhase('compose'); }}>
                  <div className="icon"><Smile size={22} strokeWidth={2.2} /></div>
                  <strong>情绪</strong>
                </button>

                {/* 技术 */}
                <button type="button" className="qn-bento-item qn-bento-tech" onClick={() => { setDiaryType('tech'); setRecordPhase('compose'); }}>
                  <div className="icon"><Code size={22} strokeWidth={2.2} /></div>
                  <strong>技术</strong>
                </button>
              </div>
            )}

            {(recordPhase === 'compose' || editingDiary) && (<>
            <div className="record-type-row">
              {TAG_OPTIONS.map(t => (
                <button
                  key={t.key}
                  type="button"
                  className={`record-type-chip ${diaryType === t.key ? 'selected' : ''}`}
                  style={{ '--chip-color': t.color, '--chip-bg': t.bgColor } as React.CSSProperties}
                  onClick={() => {
                    setDiaryType(t.key);
                    if (t.key !== 'mood') { setSelectedMood(''); setShowMoodPicker(false); }
                  }}
                >
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>

            {/* 快捷情绪栏（仅情绪类型时显示） */}
            {diaryType === 'mood' && (
            <div className="record-mood-row">
              {QUICK_MOODS.map((m) => (
                <button
                  key={m.emoji}
                  type="button"
                  className={`record-mood-btn ${selectedMood === m.emoji ? 'active' : ''}`}
                  onClick={() => {
                    setDiaryType('mood');
                    handleMoodSelect(m.emoji, m.label);
                  }}
                  style={{ '--mood-color': m.color } as React.CSSProperties}
                >
                  <span className="record-mood-emoji">{m.emoji}</span>
                  <span className="record-mood-label">{m.label}</span>
                </button>
              ))}

              {/* “更多” 打开完整心情面板 */}
              <button
                type="button"
                className="record-mood-btn more"
                onClick={() => { setDiaryType('mood'); setShowMoodPicker(v => !v); }}
                title="更多心情"
              >
                <span className="record-mood-emoji">➕</span>
                <span className="record-mood-label">更多</span>
              </button>
            </div>
            )}

            {/* 完整心情面板 */}
            {diaryType === 'mood' && showMoodPicker && (
              <div className="record-mood-panel">
                <div className="record-mood-grid">
                  {MOOD_EMOJIS.map(({ emoji, label }) => (
                    <button
                      key={emoji}
                      type="button"
                      className={`record-mood-chip ${selectedMood === emoji ? 'selected' : ''}`}
                      onClick={() => handleMoodSelect(emoji, label)}
                    >
                      <span style={{ fontSize: 22 }}>{emoji}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 关联人员（显示为chip） */}
            {selectedPersonId && (
              <div className="record-person-chip">
                <span className="record-person-avatar" style={{ background: getSelectedPerson()?.color || 'var(--primary)' }}>
                  {getSelectedPerson()?.avatar || getSelectedPerson()?.name?.charAt(0)}
                </span>
                <span className="record-person-name">@{getSelectedPerson()?.name}</span>
                <button className="record-person-remove" onClick={() => setSelectedPersonId(null)} aria-label="取消关联">✕</button>
              </div>
            )}

            {/* 快速模板 */}
            {diaryType === 'fitness' && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="record-quick-btn" onClick={() => insertTemplate('【训练部位】\n【动作/重量/组数】\n - \n【身体状态】\n - ')}>💪 训练模板</button>
                <button type="button" className="record-quick-btn" onClick={() => insertTemplate('【有氧运动】\n项目：\n时长：\n心率：')}>🏃 有氧模板</button>
              </div>
            )}
            {diaryType === 'tech' && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="record-quick-btn" onClick={() => insertTemplate('【问题/需求】\n - \n【解决方案】\n - \n【代码/参考】\n - ')}>🐛 解决问题</button>
                <button type="button" className="record-quick-btn" onClick={() => insertTemplate('```\n// 在这里粘贴代码\n```')}>💻 代码块</button>
                <button type="button" className="record-quick-btn" onClick={() => insertTemplate('【学习总结】\n1. \n2. \n3. ')}>📝 学习总结</button>
              </div>
            )}
            {diaryType === 'note' && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="record-quick-btn" onClick={() => insertTemplate('【今日闪光点】\n✨ \n【感恩的人/事】\n🙏 ')}>🌟 感恩日记</button>
                <button type="button" className="record-quick-btn" onClick={() => insertTemplate('【待办事项】\n- [ ] \n- [ ] ')}>✅ TODO列表</button>
              </div>
            )}

            {/* 输入框 */}
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleContentChange}
              className="record-textarea"
              placeholder={getPlaceholder(diaryType)}
              rows={8}
              autoFocus
            />

            {/* Mention list */}
            {showMentionList && filteredPeople.length > 0 && (
              <div className="mention-list record-mention-list">
                {filteredPeople.slice(0, 6).map(person => (
                  <div key={person.id} className="mention-item" onClick={() => handleMentionSelect(person)}>
                    <span className="person-chip-avatar" style={{ background: person.color || 'var(--primary)', width: 28, height: 28, fontSize: 11 }}>
                      {person.avatar || person.name.charAt(0)}
                    </span>
                    <span>{person.name}</span>
                  </div>
                ))}
              </div>
            )}

            {/* 隐藏文件输入 */}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
              onChange={handleFileChange}
              aria-hidden
            />

            {/* 已选图片预览 */}
            {selectedFiles.length > 0 && (
              <div className="record-file-grid">
                {selectedFiles.map((f, idx) => {
                  const url = URL.createObjectURL(f);
                  return (
                    <div key={`${f.name}_${f.size}_${f.lastModified}`} className="record-file-item">
                      <img
                        src={url}
                        alt={f.name}
                        className="record-file-img"
                        onLoad={() => URL.revokeObjectURL(url)}
                        onClick={() => setPreviewImageUrl(URL.createObjectURL(f))}
                      />
                      <button type="button" className="record-file-remove" onClick={() => removeSelectedFile(idx)} aria-label="移除图片">×</button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 时间与关联人员 */}
            <div className="record-meta">
              <div className="record-meta-field">
                <label>📅</label>
                <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              {!selectedPersonId && (
                <div className="record-meta-field">
                  <label>👤</label>
                  <select value={selectedPersonId || ''} onChange={(e) => setSelectedPersonId(Number(e.target.value) || null)}>
                    <option value="">关联人员...</option>
                    {people.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
                  </select>
                </div>
              )}
            </div>

            {/* 发布到泛灵之网 */}
            {!editingDiary && (
              <label className="record-public-toggle">
                <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
                <span className="record-public-icon">🕸️</span>
                <span className="record-public-text">发布到泛灵之网</span>
              </label>
            )}

            {/* 底部提交栏 */}
            <div className="record-foot">
              <div className="record-tools">
                <button type="button" className="record-tool-btn" onClick={triggerGalleryInput} disabled={selectedFiles.length >= 9 || saving} title="添加图片">🖼️</button>
                <span className="record-file-count">{selectedFiles.length}/9</span>
              </div>
              <button className="record-submit" onClick={handleSave} disabled={saving || !content.trim()}>
                {saving ? '铭刻中...' : '铭刻'}
              </button>
            </div>
            </>)}
          </div>
        </div>
      )}

      {aiSnapping && (
        <AiSnapScanningOverlay
          previewUrl={aiSnapScanUrl}
          phase={aiSnapPhase}
          elapsedSeconds={aiSnapElapsedSeconds}
        />
      )}

      {previewImageUrl && (
        <div role="dialog" aria-modal="true" aria-label="图片预览"
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={closePreview}>
          <img src={previewImageUrl} alt="预览" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <AiSnapResultSheet
        open={aiSnapModalOpen}
        previewUrl={aiSnapPreviewUrl}
        analysis={aiSnapAnalysis}
        suggestions={aiSnapSuggestionsLoading ? null : aiSnapSuggestions}
        mealType={aiSnapMealType}
        onChangeMealType={(v) => setAiSnapMealType(v)}
        onClose={closeAiSnapModal}
        onSave={async ({ mealType, items }) => {
          setAiSnapSaveBusy(true);
          try {
            const dateStr = toLocalDateInputValue();
            await nutritionApi.createMealsBatch({
              meal_type: mealType,
              date: dateStr,
              items: items.map((it) => {
                const n = it.nutrients || {};
                return {
                  amount: it.grams,
                  food_name: it.name,
                  calories: Number(n.calories) || 0,
                  protein: Number(n.protein) || 0,
                  fat: Number(n.fat) || 0,
                  carbs: Number(n.carbs) || 0,
                  fiber: Number(n.fiber) || 0,
                  sugar: Number(n.sugar) || 0,
                  sodium: Number(n.sodium) || 0,
                  potassium: Number(n.potassium) || 0,
                  cholesterol: Number(n.cholesterol) || 0,
                  saturated_fat: Number(n.saturated_fat) || 0,
                  unsaturated_fat: Number(n.unsaturated_fat) || 0,
                  trans_fat: Number(n.trans_fat) || 0,
                  vitamin_c: Number(n.vitamin_c) || 0,
                  calcium: Number(n.calcium) || 0,
                  iron: Number(n.iron) || 0,
                  zinc: Number(n.zinc) || 0,
                  magnesium: Number(n.magnesium) || 0,
                  phosphorus: Number(n.phosphorus) || 0,
                  selenium: Number(n.selenium) || 0,
                  vitamin_a: Number(n.vitamin_a) || 0,
                  vitamin_b1: Number(n.vitamin_b1) || 0,
                  vitamin_b2: Number(n.vitamin_b2) || 0,
                  vitamin_b6: Number(n.vitamin_b6) || 0,
                  vitamin_b12: Number(n.vitamin_b12) || 0,
                  vitamin_d: Number(n.vitamin_d) || 0,
                  vitamin_e: Number(n.vitamin_e) || 0,
                  vitamin_k: Number(n.vitamin_k) || 0,
                  folate: Number(n.folate) || 0,
                  niacin: Number(n.niacin) || 0,
                  water: Number(n.water) || 0,
                  caffeine: Number(n.caffeine) || 0,
                  alcohol: Number(n.alcohol) || 0,
                  note: 'AI快拍识别',
                };
              }),
            });
            loadData();
            onDiarySaved?.();
            closeAiSnapModal();
            closeRecordModal();
          } catch (err: any) {
            alert('保存到饮食失败: ' + (err.response?.data?.detail || err.message));
            setAiSnapSaveBusy(false);
            throw err;
          } finally {
            setAiSnapSaveBusy(false);
          }
        }}
        onOpenWiki={async (itemName) => {
          const name = String(itemName || '').trim();
          if (!name) return;
          setAiSnapWikiOpen(true);
          setAiSnapWikiTitle(name);
          setAiSnapWikiInfo(null);
          setAiSnapWikiLoading(true);
          try {
            const res = await nutritionApi.getAiSnapItemInfo(name);
            setAiSnapWikiInfo(res.data?.info || null);
          } catch {
            setAiSnapWikiInfo(null);
          } finally {
            setAiSnapWikiLoading(false);
          }
        }}
      />

      <AiSnapWikiSheet
        open={aiSnapWikiOpen}
        title={aiSnapWikiTitle}
        loading={aiSnapWikiLoading}
        info={aiSnapWikiInfo}
        onClose={() => setAiSnapWikiOpen(false)}
      />

      <WaterDrinkModal
        open={showWaterModal}
        onClose={() => setShowWaterModal(false)}
        onSuccess={onDiarySaved}
      />

      {quickHealthKind && <HealthRecordDialog
        kind={quickHealthKind}
        variant="quick"
        onClose={() => setQuickHealthKind(null)}
        onSaved={(message) => {
          setHealthToast({ message, kind: quickHealthKind });
          onDiarySaved?.();
        }}
        onOpenHistory={(kind) => { setQuickHealthKind(null); setVitalHistoryKind(kind); }}
      />}

      {vitalHistoryKind && <VitalHistoryDialog
        initialKind={vitalHistoryKind}
        onClose={() => setVitalHistoryKind(null)}
        onRecord={(kind) => { setVitalHistoryKind(null); setQuickHealthKind(kind); }}
      />}

      {healthToast && <div className="qn-health-toast" role="status" aria-live="polite">
        <span><HeartPulse size={17} />{healthToast.message}</span>
        <button type="button" onClick={() => { setVitalHistoryKind(healthToast.kind); setHealthToast(null); }}>查看趋势</button>
      </div>}

      {/* 创建入口已移到 App 底部导航栏中间的 ➕ 按钮 */}

      <style>{`
        /* ===== Dark Root Background ===== */
        .diary-feed-root {
          min-height: var(--app-height, 100vh);
          background-color: #020617;
          background-image: linear-gradient(180deg, #020617 0%, #071020 42%, #020617 100%);
          color: #f8fafc;
          padding: 18px;
          padding-bottom: calc(100px + var(--safe-area-inset-bottom, 0px));
          --surface: rgba(15, 23, 42, 0.68);
          --border: rgba(255, 255, 255, 0.08);
          --border-light: rgba(255, 255, 255, 0.06);
          --text-primary: rgba(226, 232, 240, 0.95);
          --text-secondary: rgba(148, 163, 184, 0.8);
          --text-tertiary: rgba(148, 163, 184, 0.5);
          --primary: #8b5cf6;
          --primary-bg: rgba(139, 92, 246, 0.12);
          --shadow-sm: 0 4px 30px rgba(0, 0, 0, 0.3);
          --shadow-lg: 0 8px 40px rgba(0, 0, 0, 0.5);
          --surface-hover: rgba(30, 41, 59, 0.5);
          --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
          --danger: #f43f5e;
          --danger-bg: rgba(244, 63, 94, 0.12);
        }
        @keyframes dfSpin { to { transform: rotate(360deg); } }

        /* ===== Game Bars (HP & MP) ===== */
        .df-game-bars {
          display: flex; flex-direction: column; gap: 12px;
          padding: 10px 0 24px;
        }
        .df-bar-wrap {
          display: flex; align-items: center; gap: 10px;
        }
        .df-bar-icon {
          font-size: 18px; width: 24px; text-align: center;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        }
        .df-bar-track {
          flex: 1; height: 16px; background: rgba(15,23,42,0.8);
          border-radius: 8px; border: 1px solid rgba(255,255,255,0.08);
          position: relative; overflow: hidden;
          box-shadow: inset 0 2px 6px rgba(0,0,0,0.5);
        }
        .df-bar-fill {
          height: 100%; border-radius: 6px;
          transition: width 1s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .df-bar-fill.hp {
          background: linear-gradient(90deg, #b91c1c, #f43f5e);
          box-shadow: 0 0 10px rgba(244,63,94,0.5);
        }
        .df-bar-fill.tech {
          background: linear-gradient(90deg, #0369a1, #38bdf8);
          box-shadow: 0 0 10px rgba(56,189,248,0.5);
        }
        .df-bar-text {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          font-size: 10px; font-weight: 800; color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.8);
          letter-spacing: 0.5px;
        }

        .diary-feed {
          max-width: 880px;
          margin: 0 auto;
        }

        .df-filter-panel {
          margin-bottom: 24px;
          padding: 18px;
          border-radius: 18px;
          background: #0b1224;
          border: 1px solid rgba(148,163,184,0.16);
          box-shadow: 0 16px 40px rgba(0,0,0,0.24);
        }
        .df-filter-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 16px;
        }
        .df-filter-head span {
          display: block;
          color: rgba(148,163,184,0.72);
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.08em;
        }
        .df-filter-head strong {
          display: block;
          margin-top: 2px;
          color: rgba(226,232,240,0.96);
          font-size: 17px;
          font-weight: 850;
        }
        .df-filter-head small {
          color: rgba(125,211,252,0.78);
          font-size: 12px;
          font-weight: 800;
        }
        .df-filter-row {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 10px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .df-filter-row::-webkit-scrollbar { display: none; }
        .df-filter-chip {
          min-width: 0;
          min-height: 48px;
          display: flex; align-items: center; justify-content: center; gap: 5px;
          padding: 0 10px;
          border-radius: 12px;
          border: 1px solid rgba(148,163,184,0.18);
          background: #070d1b;
          font-size: 12px; font-weight: 800; color: rgba(203,213,225,0.72);
          cursor: pointer; white-space: nowrap; font-family: inherit;
          transition: background 180ms, border-color 180ms, color 180ms, transform 120ms;
        }
        .df-filter-chip.active {
          background: rgba(139,92,246,0.22);
          color: white;
          border-color: rgba(139,92,246,0.72);
          box-shadow: inset 0 0 0 1px rgba(167,139,250,0.12);
        }
        .df-filter-chip:active { transform: scale(0.95); }

        /* ===== Tag Row in Editor ===== */
        .df-tag-row {
          display: flex; gap: 8px; padding: 0 24px 16px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .df-tag-row::-webkit-scrollbar { display: none; }
        .df-tag-chip {
          display: flex; align-items: center; gap: 5px;
          padding: 7px 14px; border-radius: 20px;
          border: 1.5px solid var(--border); background: var(--surface);
          font-size: 13px; font-weight: 500; color: var(--text-secondary);
          cursor: pointer; white-space: nowrap; font-family: inherit;
          transition: all 200ms;
        }
        .df-tag-chip.selected {
          background: var(--tag-bg); color: var(--tag-color);
          border-color: var(--tag-color);
          font-weight: 600;
        }
        .df-tag-chip:active { transform: scale(0.95); }

        /* ===== Record Modal (dark mystic style) ===== */
        .record-modal {
          position: fixed; inset: 0; overflow-y: auto;
          background: rgba(2, 6, 23, 0.9);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          z-index: 1200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: recordFadeIn 180ms ease;
        }
        @keyframes recordFadeIn { from { opacity: 0; } to { opacity: 1; } }

        .record-sheet {
          width: 100%;
          max-width: 560px;
          max-height: 80vh;
          position: relative;
          background: rgba(15, 23, 42, 0.96);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 22px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 14px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
          animation: recordSheetIn 260ms cubic-bezier(0.34, 1.56, 0.64, 1);
          overflow-y: auto;
        }
        @keyframes recordSheetIn { from { transform: scale(0.95); opacity: 0.6; } to { transform: scale(1); opacity: 1; } }

        .record-head {
          display: grid;
          grid-template-columns: 40px 1fr 40px;
          align-items: center;
          gap: 4px;
        }
        .record-head-side { min-height: 36px; display: flex; align-items: center; justify-content: flex-start; }
        .record-title { margin: 0; font-size: 18px; font-weight: 800; color: rgba(226,232,240,0.95); letter-spacing: 0.2px; text-align: center; }
        .record-back {
          width: 36px; height: 36px; border-radius: 10px; border: none;
          background: rgba(30,41,59,0.85); color: rgba(226,232,240,0.95);
          font-size: 18px; line-height: 1; cursor: pointer;
        }
        .record-close { justify-self: end; }

        /* ================= Bento Grid ================= */
        .qn-bento {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          grid-auto-rows: 64px;
          gap: 12px;
          padding: 6px 0 20px;
        }
        .qn-bento-item {
          border-radius: 20px;
          background: rgba(15,23,42,0.55);
          border: 1px solid rgba(148,163,184,0.12);
          text-decoration: none;
          color: var(--text-primary);
          cursor: pointer;
          transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s, background 0.15s;
          display: flex;
          overflow: hidden;
          position: relative;
          font-family: inherit;
          text-align: left;
        }
        .qn-bento-item:hover {
          transform: translateY(-2px);
          background: rgba(30,41,59,0.7);
          border-color: rgba(148,163,184,0.25);
          box-shadow: 0 10px 24px rgba(0,0,0,0.2);
        }
        .qn-bento-item strong {
          font-size: 0.95rem;
          font-weight: 800;
          line-height: 1.2;
        }
        .qn-bento-item span {
          font-size: 0.72rem;
          color: var(--text-secondary);
          margin-top: 4px;
          line-height: 1.3;
        }
        .qn-bento-ai {
          grid-column: span 2;
          grid-row: span 2;
          flex-direction: row;
          align-items: center;
          padding: 24px;
          gap: 20px;
          background: linear-gradient(135deg, rgba(139,92,246,0.25), rgba(6,182,212,0.12));
          border-color: rgba(139,92,246,0.38);
          box-shadow: 0 12px 32px rgba(139,92,246,0.15);
        }
        .qn-bento-ai:hover {
          border-color: rgba(139,92,246,0.55);
          box-shadow: 0 16px 40px rgba(139,92,246,0.25);
        }
        .qn-bento-ai .icon > svg { color: #fff; }
        .qn-bento-meal .icon > svg { color: #6ee7b7; }
        .qn-bento-fitness .icon > svg { color: #fca5a5; }
        .qn-bento-water .icon > svg { color: #7dd3fc; }
        .qn-bento-note .icon > svg { color: #93c5fd; }
        .qn-bento-mood .icon > svg { color: #c4b5fd; }
        .qn-bento-tech .icon > svg { color: #fcd34d; }
        .qn-bento-ai .icon {
          width: 64px; height: 64px;
          border-radius: 20px;
          background: linear-gradient(135deg, #8b5cf6, #d946ef);
          font-size: 2rem;
          display: grid;
          place-items: center;
          box-shadow: 0 10px 24px rgba(139,92,246,0.4);
          flex-shrink: 0;
        }
        .qn-bento-ai .info {
          display: flex;
          flex-direction: column;
        }
        .qn-bento-ai strong {
          font-size: 1.25rem;
          color: #fff;
          margin-bottom: 2px;
        }
        .qn-bento-ai span {
          font-size: 0.85rem;
          color: #cbd5e1;
        }
        .qn-bento-ai .deco {
          position: absolute; right: -8px; bottom: -16px;
          color: #e9d5ff; opacity: 0.16; transform: rotate(-15deg);
          pointer-events: none;
        }
        .qn-bento-meal {
          grid-column: span 1;
          grid-row: span 2;
          flex-direction: column;
          padding: 18px 16px;
          justify-content: space-between;
          background: linear-gradient(180deg, rgba(52,211,153,0.12), rgba(15,23,42,0.55));
          border-color: rgba(52,211,153,0.22);
        }
        .qn-bento-meal .icon { font-size: 2.2rem; }
        .qn-bento-meal .info { display: flex; flex-direction: column; }
        .qn-bento-fitness {
          grid-column: span 1;
          grid-row: span 2;
          flex-direction: column;
          padding: 18px 16px;
          justify-content: space-between;
          background: linear-gradient(180deg, rgba(244,63,94,0.12), rgba(15,23,42,0.55));
          border-color: rgba(244,63,94,0.22);
        }
        .qn-bento-fitness .icon { font-size: 2.2rem; }
        .qn-bento-fitness .info { display: flex; flex-direction: column; }
        .qn-bento-health {
          grid-column: span 1;
          grid-row: span 1;
          min-width: 0;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 16px;
        }
        .qn-bento-bp {
          background: linear-gradient(135deg, rgba(56,189,248,0.16), rgba(15,23,42,0.66));
          border-color: rgba(56,189,248,0.28);
        }
        .qn-bento-hr {
          background: linear-gradient(135deg, rgba(251,113,133,0.15), rgba(15,23,42,0.66));
          border-color: rgba(251,113,133,0.25);
        }
        .qn-health-icon {
          width: 36px;
          height: 36px;
          display: grid;
          place-items: center;
          flex: 0 0 36px;
          margin: 0 !important;
          border-radius: 11px;
          color: #bae6fd !important;
          background: rgba(56,189,248,0.13);
        }
        .qn-bento-hr .qn-health-icon { color: #fecdd3 !important; background: rgba(251,113,133,0.12); }
        .qn-health-info { min-width: 0; display: grid; gap: 2px; margin: 0 !important; }
        .qn-health-info strong { color: #f8fafc; }
        .qn-health-info small { overflow: hidden; color: rgba(148,163,184,0.68); font-size: 0.63rem; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
        .qn-health-trend { flex: 0 0 auto; margin-left: auto; color: rgba(125,211,252,0.55); }
        .qn-bento-water, .qn-bento-note, .qn-bento-mood, .qn-bento-tech {
          grid-column: span 1;
          grid-row: span 1;
          flex-direction: row;
          align-items: center;
          padding: 12px 16px;
          gap: 12px;
        }
        .qn-bento-water .icon, .qn-bento-note .icon, .qn-bento-mood .icon, .qn-bento-tech .icon {
          font-size: 1.6rem;
          flex-shrink: 0;
        }
        .qn-health-toast {
          position: fixed;
          left: 50%;
          bottom: calc(92px + env(safe-area-inset-bottom, 0px));
          z-index: 1400;
          width: min(360px, calc(100vw - 28px));
          min-height: 50px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 8px 9px 8px 13px;
          border: 1px solid rgba(110,231,183,0.25);
          border-radius: 14px;
          background: rgba(8,15,36,0.97);
          color: #d1fae5;
          box-shadow: 0 16px 46px rgba(0,0,0,0.5);
          transform: translateX(-50%);
          box-sizing: border-box;
        }
        .qn-health-toast span { display: flex; align-items: center; gap: 7px; margin: 0; color: #d1fae5; font-size: 12px; font-weight: 800; }
        .qn-health-toast button { min-height: 34px; padding: 0 10px; border: 1px solid rgba(56,189,248,0.25); border-radius: 9px; background: rgba(56,189,248,0.09); color: #7dd3fc; font: inherit; font-size: 11px; font-weight: 800; cursor: pointer; }
        .my-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid rgba(226,232,240,0.18);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: dfSpin 0.8s linear infinite;
        }

        /* ===== AI Snap Result Modal ===== */
        .ai-snap-sheet { max-width: 720px; }
        .ai-snap-grid { display: grid; grid-template-columns: 1fr; gap: 14px; }
        @media (min-width: 640px) {
          .ai-snap-grid { grid-template-columns: 220px 1fr; gap: 16px; }
        }
        .ai-snap-photo {
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(148,163,184,0.16);
          background: rgba(2,6,23,0.35);
          box-shadow: 0 18px 50px rgba(0,0,0,0.35);
        }
        .ai-snap-photo img { width: 100%; height: 100%; display: block; object-fit: cover; aspect-ratio: 3/4; }
        .ai-snap-photo-empty { padding: 24px; color: rgba(148,163,184,0.7); text-align: center; }
        .ai-snap-panel {
          border-radius: 18px;
          border: 1px solid rgba(148,163,184,0.14);
          background: linear-gradient(180deg, rgba(15,23,42,0.65), rgba(2,6,23,0.35));
          padding: 16px;
        }
        .ai-snap-title { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
        .ai-snap-food { font-size: 16px; font-weight: 900; color: rgba(226,232,240,0.95); }
        .ai-snap-sub { margin-top: 4px; font-size: 12px; color: rgba(148,163,184,0.65); line-height: 1.4; }
        .ai-snap-total { text-align: right; min-width: 80px; }
        .ai-snap-kcal { font-size: 22px; font-weight: 900; color: #38bdf8; line-height: 1; }
        .ai-snap-unit { font-size: 11px; color: rgba(148,163,184,0.7); margin-top: 2px; }
        .ai-snap-macros {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-top: 14px;
        }
        @media (min-width: 640px) {
          .ai-snap-macros { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        .ai-snap-macro {
          border: 1px solid rgba(148,163,184,0.12);
          border-radius: 14px;
          background: rgba(15,23,42,0.55);
          padding: 10px 12px;
        }
        .ai-snap-macro-top { display: flex; justify-content: space-between; gap: 10px; }
        .ai-snap-macro-label { font-size: 12px; color: rgba(148,163,184,0.75); font-weight: 700; }
        .ai-snap-macro-val { font-size: 13px; font-weight: 900; }
        .ai-snap-sections { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
        .ai-snap-section {
          border-radius: 14px;
          border: 1px solid rgba(148,163,184,0.12);
          background: rgba(15,23,42,0.5);
          overflow: hidden;
        }
        .ai-snap-section > summary {
          padding: 10px 12px;
          cursor: pointer;
          font-weight: 800;
          font-size: 13px;
          color: rgba(226,232,240,0.92);
          list-style: none;
        }
        .ai-snap-section > summary::-webkit-details-marker { display: none; }
        .ai-snap-nutrients {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          padding: 10px 12px 12px;
        }
        @media (min-width: 640px) {
          .ai-snap-nutrients { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        .ai-snap-nutrient {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          padding: 8px 10px;
          border-radius: 12px;
          background: rgba(2,6,23,0.35);
          border: 1px solid rgba(148,163,184,0.08);
        }
        .ai-snap-nutrient .k { font-size: 12px; color: rgba(148,163,184,0.75); font-weight: 700; }
        .ai-snap-nutrient .v { font-size: 12px; color: rgba(226,232,240,0.92); font-weight: 800; }
        .ai-snap-items { padding: 10px 12px 12px; display: flex; flex-direction: column; gap: 8px; }
        .ai-snap-items-section {
          border-color: rgba(139,92,246,0.3) !important;
          background: rgba(139,92,246,0.05) !important;
        }
        .ai-snap-items-section > summary {
          color: #a78bfa !important;
          font-size: 14px !important;
        }
        .ai-snap-item {
          border-radius: 14px;
          background: rgba(2,6,23,0.35);
          border: 1px solid rgba(148,163,184,0.12);
          padding: 10px 12px;
          transition: opacity 200ms, border-color 200ms;
        }
        .ai-snap-item-unchecked {
          opacity: 0.4;
          border-color: rgba(148,163,184,0.06);
        }
        .ai-snap-item-top { display: flex; align-items: center; gap: 8px; }
        .ai-snap-item-check {
          display: flex; align-items: center; flex-shrink: 0;
        }
        .ai-snap-item-check input[type="checkbox"] {
          width: 18px; height: 18px; accent-color: #8b5cf6;
          cursor: pointer; border-radius: 4px;
        }
        .ai-snap-item-name-input {
          flex: 1;
          min-width: 0;
          background: rgba(15,23,42,0.6);
          border: 1px solid rgba(148,163,184,0.14);
          border-radius: 8px;
          padding: 6px 8px;
          font-size: 13px;
          font-weight: 800;
          color: rgba(226,232,240,0.95);
          font-family: inherit;
        }
        .ai-snap-item-name-input:focus {
          outline: none;
          border-color: rgba(139,92,246,0.5);
          box-shadow: 0 0 0 2px rgba(139,92,246,0.15);
        }
        .ai-snap-item-grams {
          display: flex; align-items: center; gap: 2px; flex-shrink: 0;
        }
        .ai-snap-item-grams-input {
          width: 52px;
          background: rgba(15,23,42,0.6);
          border: 1px solid rgba(148,163,184,0.14);
          border-radius: 8px;
          padding: 6px 6px;
          font-size: 12px;
          font-weight: 800;
          color: rgba(226,232,240,0.95);
          text-align: right;
          font-family: inherit;
        }
        .ai-snap-item-grams-input:focus {
          outline: none;
          border-color: rgba(139,92,246,0.5);
          box-shadow: 0 0 0 2px rgba(139,92,246,0.15);
        }
        .ai-snap-item-grams-unit {
          font-size: 11px; color: rgba(148,163,184,0.7); font-weight: 700;
        }
        .ai-snap-item-wiki-btn {
          flex-shrink: 0;
          width: 30px; height: 30px;
          border-radius: 8px;
          border: 1px solid rgba(148,163,184,0.12);
          background: rgba(15,23,42,0.5);
          cursor: pointer;
          font-size: 14px;
          display: grid; place-items: center;
          transition: background 150ms, border-color 150ms;
        }
        .ai-snap-item-wiki-btn:hover {
          background: rgba(139,92,246,0.15);
          border-color: rgba(139,92,246,0.4);
        }
        .ai-snap-item-macros { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 6px; font-size: 12px; color: rgba(148,163,184,0.85); padding-left: 26px; }
        .ai-snap-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 14px;
          align-items: center;
        }
        @media (min-width: 640px) {
          .ai-snap-actions { grid-template-columns: 1fr auto auto; }
        }
        .ai-snap-mealtype {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 14px;
          background: rgba(15,23,42,0.55);
          border: 1px solid rgba(148,163,184,0.12);
        }
        .ai-snap-mealtype .lbl { font-size: 12px; color: rgba(148,163,184,0.75); font-weight: 800; }
        .ai-snap-mealtype select {
          flex: 1;
          background: rgba(2,6,23,0.35);
          border: 1px solid rgba(148,163,184,0.14);
          color: rgba(226,232,240,0.95);
          border-radius: 10px;
          padding: 8px 10px;
          font-family: inherit;
        }
        .ai-snap-btn {
          border-radius: 14px;
          padding: 10px 12px;
          font-weight: 900;
          cursor: pointer;
          font-family: inherit;
          border: 1px solid rgba(148,163,184,0.14);
        }
        .ai-snap-btn.primary {
          background: linear-gradient(135deg, #0369a1, #38bdf8);
          color: #0b1220;
          border-color: rgba(56,189,248,0.35);
        }
        .ai-snap-btn.ghost {
          background: rgba(15,23,42,0.55);
          color: rgba(226,232,240,0.9);
        }
        .ai-snap-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* ===== Tab switcher ===== */
        .ai-snap-tabs {
          display: flex;
          gap: 8px;
          padding: 0 0 14px;
          border-bottom: 1px solid rgba(148,163,184,0.1);
          margin-bottom: 14px;
        }
        .ai-snap-tab {
          flex: 1;
          padding: 8px 12px;
          border-radius: 14px;
          font-size: 13px;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          border: 1.5px solid rgba(148,163,184,0.16);
          background: rgba(15,23,42,0.45);
          color: rgba(148,163,184,0.75);
          transition: all 160ms;
        }
        .ai-snap-tab.active {
          background: linear-gradient(135deg, rgba(3,105,161,0.6), rgba(56,189,248,0.25));
          border-color: rgba(56,189,248,0.4);
          color: #38bdf8;
        }

        /* ===== Wiki panel ===== */
        .ai-snap-wiki {
          margin-top: 14px;
          min-height: 120px;
        }
        .ai-snap-wiki-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 32px 0;
          color: rgba(148,163,184,0.7);
          font-size: 13px;
        }
        .ai-snap-wiki-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .ai-snap-wiki-section {
          border-radius: 14px;
          border: 1px solid rgba(148,163,184,0.12);
          background: rgba(15,23,42,0.5);
          padding: 12px 14px;
        }
        .ai-snap-wiki-label {
          font-size: 12px;
          font-weight: 800;
          color: #38bdf8;
          margin-bottom: 6px;
        }
        .ai-snap-wiki-text {
          font-size: 13px;
          color: rgba(226,232,240,0.85);
          line-height: 1.65;
          margin: 0;
        }
        .ai-snap-wiki-empty {
          padding: 32px 0;
          text-align: center;
          color: rgba(148,163,184,0.6);
          font-size: 13px;
        }

        /* Suggestions */
        .ai-snap-sug-loading {
          display: flex; align-items: center; gap: 10px;
          padding: 18px 0; justify-content: center;
          color: rgba(148,163,184,0.6); font-size: 13px;
        }
        .ai-snap-sug-loading .my-spinner { width: 20px; height: 20px; border-width: 2px; }
        .ai-snap-sug { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
        .ai-snap-sug-overall {
          display: flex; align-items: baseline; gap: 10px;
          padding: 10px 14px; border-radius: 12px;
          background: rgba(139,92,246,0.08); border: 1px solid rgba(139,92,246,0.18);
        }
        .ai-snap-sug-rating {
          font-size: 14px; font-weight: 800; color: #a78bfa; white-space: nowrap;
        }
        .ai-snap-sug-note { font-size: 12px; color: rgba(226,232,240,0.7); line-height: 1.5; }
        .ai-snap-sug-card {
          padding: 10px 14px; border-radius: 12px;
          border-left: 3px solid; background: rgba(15,23,42,0.5);
        }
        .ai-snap-sug-warning { border-left-color: #f59e0b; background: rgba(245,158,11,0.06); }
        .ai-snap-sug-positive { border-left-color: #22c55e; background: rgba(34,197,94,0.06); }
        .ai-snap-sug-tip { border-left-color: #3b82f6; background: rgba(59,130,246,0.06); }
        .ai-snap-sug-head { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
        .ai-snap-sug-icon { font-size: 14px; flex-shrink: 0; }
        .ai-snap-sug-title { font-size: 13px; font-weight: 700; color: rgba(226,232,240,0.95); }
        .ai-snap-sug-detail { font-size: 12px; color: rgba(226,232,240,0.7); line-height: 1.6; margin: 0; }

        /* Type selector row */
        .record-type-row {
          display: flex; gap: 6px; overflow-x: auto;
          padding: 2px 0; -webkit-overflow-scrolling: touch;
        }
        .record-type-row::-webkit-scrollbar { display: none; }
        .record-type-chip {
          display: flex; align-items: center; gap: 4px;
          padding: 6px 12px; border-radius: 16px;
          border: 1.5px solid rgba(148,163,184,0.22);
          background: rgba(30,41,59,0.6);
          font-size: 12px; font-weight: 500;
          color: rgba(148,163,184,0.9);
          cursor: pointer; white-space: nowrap; font-family: inherit;
          transition: all 180ms;
        }
        .record-type-chip:active { transform: scale(0.94); }
        .record-type-chip.selected {
          background: var(--chip-bg, rgba(139,92,246,0.15));
          color: var(--chip-color, #A29BFE);
          border-color: var(--chip-color, #A29BFE);
          font-weight: 700;
        }

        .record-close {
          width: 34px; height: 34px; border-radius: 999px;
          border: 1px solid rgba(148,163,184,0.25);
          background: rgba(30,41,59,0.8);
          color: rgba(148,163,184,0.95);
          cursor: pointer;
          font-size: 16px;
        }
        .record-close:active { transform: scale(0.92); }

        .record-mood-row {
          display: flex;
          gap: 10px;
          justify-content: space-between;
          padding: 2px 2px;
        }
        .record-mood-btn {
          flex: 1;
          min-width: 0;
          border: none;
          background: transparent;
          color: rgba(148,163,184,0.95);
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 6px 0;
          transition: transform 120ms ease;
          font-family: inherit;
        }
        .record-mood-btn:active { transform: scale(0.92); }
        .record-mood-btn.active { color: var(--mood-color); transform: scale(1.06); }
        .record-mood-btn.more { opacity: 0.9; }
        .record-mood-emoji { font-size: 22px; line-height: 1; }
        .record-mood-label { font-size: 11px; letter-spacing: 0.2px; }

        .record-mood-panel {
          border: 1px solid rgba(139, 92, 246, 0.22);
          border-radius: 14px;
          background: linear-gradient(180deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.86) 100%);
          padding: 12px;
          overflow: hidden;
        }
        .record-mood-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
        .record-mood-chip {
          border-radius: 12px;
          border: 1px solid rgba(148,163,184,0.18);
          background: rgba(30,41,59,0.5);
          padding: 10px 6px;
          cursor: pointer;
          display: flex; flex-direction: column; align-items: center; gap: 4px;
          transition: transform 120ms ease, border-color 150ms ease;
          font-family: inherit;
        }
        .record-mood-chip:active { transform: scale(0.92); }
        .record-mood-chip.selected { border-color: rgba(139,92,246,0.65); background: rgba(139,92,246,0.12); }

        .record-person-chip {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          width: fit-content;
          background: rgba(139,92,246,0.10);
          border: 1px solid rgba(139,92,246,0.22);
          padding: 8px 12px 8px 8px;
          border-radius: 999px;
        }
        .record-person-avatar {
          width: 26px; height: 26px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 11px; font-weight: 700;
        }
        .record-person-name { font-size: 12px; color: rgba(226,232,240,0.9); font-weight: 600; }
        .record-person-remove {
          border: none;
          background: transparent;
          color: rgba(148,163,184,0.95);
          cursor: pointer;
          font-size: 14px;
          padding: 0 4px;
        }

        .record-textarea {
          width: 100%;
          flex: 1;
          border-radius: 14px;
          padding: 14px;
          border: 1px solid rgba(148,163,184,0.22);
          background: rgba(30,41,59,0.8);
          color: rgba(226,232,240,0.95);
          outline: none;
          resize: none;
          line-height: 1.7;
          font-size: 14px;
          font-family: inherit;
          box-shadow: inset 0 0 0 1px rgba(2,6,23,0.2);
        }
        .record-textarea:focus {
          border-color: rgba(139, 92, 246, 0.65);
          box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.25);
        }

        .record-quick-btn {
          padding: 6px 12px;
          border-radius: 8px;
          border: 1px solid rgba(139,92,246,0.3);
          background: rgba(139,92,246,0.1);
          color: rgba(226,232,240,0.9);
          font-size: 12px;
          cursor: pointer;
          transition: all 150ms;
          font-family: inherit;
        }
        .record-quick-btn:hover { background: rgba(139,92,246,0.2); }
        .record-quick-btn:active { transform: scale(0.95); }

        .record-mention-list {
          position: absolute;
          left: 24px;
          right: 24px;
          bottom: 160px;
          width: auto;
        }

        .record-file-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .record-file-item { position: relative; }
        .record-file-img {
          width: 100%;
          height: 86px;
          object-fit: cover;
          border-radius: 12px;
          border: 1px solid rgba(148,163,184,0.18);
          cursor: pointer;
          display: block;
        }
        .record-file-remove {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 22px;
          height: 22px;
          border-radius: 999px;
          border: none;
          background: rgba(0,0,0,0.55);
          color: white;
          cursor: pointer;
          line-height: 22px;
          font-size: 14px;
        }

        .record-foot {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }
        .record-tools { display:flex; gap: 10px; align-items:center; color: rgba(148,163,184,0.9); }
        .record-tool-btn {
          width: 34px; height: 34px;
          border-radius: 10px;
          border: 1px solid rgba(148,163,184,0.20);
          background: rgba(30,41,59,0.7);
          color: rgba(148,163,184,0.95);
          cursor: pointer;
          font-size: 16px;
        }
        .record-tool-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .record-tool-btn:active { transform: scale(0.92); }
        .record-file-count { font-size: 11px; opacity: 0.85; }
        .record-submit {
          border: none;
          border-radius: 999px;
          padding: 10px 18px;
          font-weight: 800;
          font-family: inherit;
          color: white;
          cursor: pointer;
          background: linear-gradient(90deg, #7C3AED, #D946EF);
          box-shadow: 0 0 18px rgba(139,92,246,0.35);
        }
        .record-submit:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
        .record-submit:active { transform: scale(0.97); }

        .record-meta {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          padding-top: 4px;
        }
        .record-meta-field {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 170px;
        }
        .record-meta-field label { font-size: 12px; opacity: 0.9; }
        .record-meta-field input, .record-meta-field select {
          flex: 1;
          border-radius: 12px;
          padding: 10px 12px;
          border: 1px solid rgba(148,163,184,0.22);
          background: rgba(30,41,59,0.65);
          color: rgba(226,232,240,0.95);
          font-family: inherit;
          font-size: 13px;
        }
        .record-meta-field input:focus, .record-meta-field select:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.6);
          box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.18);
        }

        /* ===== Public Toggle (泛灵之网) ===== */
        .record-public-toggle {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 14px; border-radius: 12px;
          background: rgba(139,92,246,0.08);
          border: 1px solid rgba(139,92,246,0.18);
          cursor: pointer; user-select: none;
          transition: all 180ms;
        }
        .record-public-toggle:has(input:checked) {
          background: rgba(139,92,246,0.15);
          border-color: rgba(139,92,246,0.4);
        }
        .record-public-toggle input[type="checkbox"] {
          width: 16px; height: 16px; accent-color: #8b5cf6;
          cursor: pointer; margin: 0;
        }
        .record-public-icon { font-size: 16px; }
        .record-public-text {
          font-size: 13px; font-weight: 600;
          color: rgba(226,232,240,0.85);
        }

        /* record-fab removed (compose entry moved to bottom nav center button) */

        /* ===== Record Tabs ===== */
        .rec-tabs {
          display: flex; gap: 4px; margin-bottom: 14px;
          padding: 3px; border-radius: 12px;
          background: rgba(15,23,42,0.6);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .rec-tab {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
          padding: 9px 8px; border: none; border-radius: 10px;
          background: transparent; color: rgba(148,163,184,0.55);
          font-size: 13px; font-weight: 600; cursor: pointer;
          font-family: inherit; transition: all 200ms;
        }
        .rec-tab.active {
          background: rgba(139,92,246,0.18); color: #c4b5fd;
          box-shadow: 0 2px 8px rgba(139,92,246,0.15);
        }
        .rec-tab:active { transform: scale(0.96); }

        /* ===== Quick Meal (inside record sheet) ===== */
        .qm-body { padding: 0; overflow-y: auto; flex: 1; }

        .qm-meal-types { display: flex; gap: 6px; margin-bottom: 12px; }
        .qm-mt-btn {
          flex: 1; padding: 8px 4px; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 8px; background: transparent;
          color: rgba(148,163,184,0.6); font-size: 11px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .qm-mt-btn.active { background: rgba(16,185,129,0.1); }

        .qm-toggle-row { display: flex; gap: 6px; margin-bottom: 12px; }
        .qm-toggle {
          flex: 1; padding: 8px; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 8px; background: transparent;
          color: rgba(148,163,184,0.5); font-size: 12px; font-weight: 600;
          cursor: pointer; font-family: inherit;
        }
        .qm-toggle.active { background: rgba(139,92,246,0.15); color: #a78bfa; border-color: rgba(139,92,246,0.3); }

        .qm-search { margin-bottom: 10px; }
        .qm-search input {
          width: 100%; padding: 10px 14px; box-sizing: border-box;
          border: 1px solid rgba(51,65,85,0.5); border-radius: 10px;
          background: rgba(15,23,42,0.8); color: #f8fafc;
          font-size: 14px; font-family: inherit;
        }
        .qm-search input:focus { outline: none; border-color: rgba(16,185,129,0.5); }

        .qm-cats {
          display: flex; gap: 4px; overflow-x: auto; padding-bottom: 8px;
          margin-bottom: 10px; -webkit-overflow-scrolling: touch;
        }
        .qm-cats::-webkit-scrollbar { display: none; }
        .qm-cat {
          padding: 5px 10px; border: 1px solid rgba(51,65,85,0.4);
          border-radius: 6px; background: transparent;
          color: rgba(148,163,184,0.5); font-size: 11px; font-weight: 600;
          cursor: pointer; white-space: nowrap; font-family: inherit;
        }
        .qm-cat.active { background: rgba(16,185,129,0.15); color: #10b981; border-color: rgba(16,185,129,0.3); }

        .qm-food-list { max-height: 220px; overflow-y: auto; margin-bottom: 12px; }
        .qm-food {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; margin-bottom: 4px;
          background: rgba(15,23,42,0.5); border: 1px solid rgba(51,65,85,0.3);
          border-radius: 10px; cursor: pointer; transition: all 0.15s;
        }
        .qm-food:active { transform: scale(0.98); }
        .qm-food.selected { border-color: rgba(16,185,129,0.5); background: rgba(16,185,129,0.08); }
        .qm-food-icon { font-size: 24px; flex-shrink: 0; }
        .qm-food-info { flex: 1; min-width: 0; }
        .qm-food-name { display: block; font-size: 13px; font-weight: 600; color: rgba(226,232,240,0.9); }
        .qm-food-cal { display: block; font-size: 11px; color: rgba(148,163,184,0.5); margin-top: 1px; }
        .qm-food-macros { display: flex; gap: 4px; flex-shrink: 0; }
        .qm-fm { font-size: 9px; padding: 2px 5px; border-radius: 4px; font-weight: 600; }
        .qm-fm.p { background: rgba(59,130,246,0.15); color: #60a5fa; }
        .qm-fm.f { background: rgba(245,158,11,0.15); color: #fbbf24; }
        .qm-fm.c { background: rgba(16,185,129,0.15); color: #34d399; }
        .qm-empty { text-align: center; padding: 20px; color: rgba(148,163,184,0.4); font-size: 13px; }

        .qm-amount-bar {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; margin-bottom: 12px;
          background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2);
          border-radius: 10px;
        }
        .qm-amount-label { font-size: 13px; font-weight: 600; color: rgba(226,232,240,0.9); flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .qm-amount-input-wrap { display: flex; align-items: center; gap: 4px; }
        .qm-amount-input {
          width: 60px; padding: 6px 8px; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 6px; background: rgba(15,23,42,0.8); color: #f8fafc;
          font-size: 14px; font-family: inherit; text-align: center;
        }
        .qm-amount-input:focus { outline: none; border-color: rgba(16,185,129,0.5); }
        .qm-amount-unit { font-size: 12px; color: rgba(148,163,184,0.5); }
        .qm-amount-cal { font-size: 12px; font-weight: 600; color: #10b981; white-space: nowrap; }

        .qm-custom { margin-bottom: 12px; }
        .qm-field { margin-bottom: 12px; }
        .qm-field label { display: block; font-size: 12px; font-weight: 600; color: rgba(226,232,240,0.7); margin-bottom: 4px; }
        .qm-field input {
          width: 100%; padding: 10px 12px; box-sizing: border-box;
          border: 1px solid rgba(51,65,85,0.5); border-radius: 8px;
          background: rgba(15,23,42,0.8); color: #f8fafc;
          font-size: 14px; font-family: inherit;
        }
        .qm-field input:focus { outline: none; border-color: rgba(16,185,129,0.5); }
        .qm-field-row { display: flex; gap: 10px; }
        .qm-field-row .qm-field { flex: 1; }

        .qm-submit {
          width: 100%; padding: 14px; border: none; border-radius: 12px;
          background: #10b981; color: white;
          font-size: 15px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
          margin-bottom: var(--safe-area-inset-bottom, 16px);
        }
        .qm-submit:disabled { opacity: 0.5; }
        .qm-submit:active:not(:disabled) { transform: scale(0.98); }

        /* Toast */
        .qm-toast {
          position: fixed;
          bottom: calc(80px + 90px + var(--safe-area-inset-bottom, 0px));
          left: 50%; transform: translateX(-50%);
          padding: 10px 24px; border-radius: 999px;
          background: rgba(17,24,39,0.95); color: #f8fafc;
          font-size: 14px; font-weight: 600;
          box-shadow: 0 4px 20px rgba(0,0,0,0.4);
          border: 1px solid rgba(52,211,153,0.3);
          z-index: 2000;
          animation: toastIn 300ms cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes toastIn { from { opacity: 0; transform: translateX(-50%) translateY(10px) scale(0.9); } to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); } }

        /* ===== Water Tab ===== */
        .wt-body {
          display: flex; flex-direction: column; align-items: center;
          padding: 20px 0 10px; gap: 20px;
        }
        .wt-loading {
          display: flex; flex-direction: column; align-items: center;
          padding: 40px 0; gap: 12px;
        }
        .wt-spinner {
          width: 28px; height: 28px;
          border: 3px solid rgba(51,65,85,0.5); border-top-color: #38bdf8;
          border-radius: 50%; animation: dfSpin 0.8s linear infinite;
        }
        .wt-ring-wrap {
          position: relative; width: 160px; height: 160px;
        }
        .wt-ring-text {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
        }
        .wt-ring-num {
          font-size: 40px; font-weight: 800; color: #38bdf8; line-height: 1;
          text-shadow: 0 0 16px rgba(56,189,248,0.4);
        }
        .wt-ring-unit {
          font-size: 13px; color: rgba(148,163,184,0.6); margin-top: 4px; font-weight: 500;
        }
        .wt-cups-row {
          display: flex; flex-wrap: wrap; justify-content: center; gap: 6px;
          max-width: 280px;
        }
        .wt-cup {
          font-size: 22px; opacity: 0.18; transition: opacity 0.3s, transform 0.3s;
        }
        .wt-cup.filled { opacity: 1; transform: scale(1.1); }
        .wt-add-btn {
          padding: 14px 48px; border: none; border-radius: 999px;
          background: linear-gradient(135deg, #0ea5e9, #38bdf8);
          color: white; font-size: 16px; font-weight: 700;
          cursor: pointer; font-family: inherit;
          box-shadow: 0 4px 20px rgba(56,189,248,0.35);
          transition: transform 150ms;
        }
        .wt-add-btn:active { transform: scale(0.95); }
        .wt-done {
          padding: 8px 20px; border-radius: 999px;
          background: rgba(52,211,153,0.12); color: #34d399;
          font-size: 13px; font-weight: 600;
          border: 1px solid rgba(52,211,153,0.2);
        }

        /* Mood Picker */
        .mood-picker { margin-bottom: 16px; }
        .mood-picker-title { font-size: 14px; color: var(--text-secondary); margin-bottom: 12px; text-align: center; font-weight: 500; }
        .mood-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
        .mood-chip {
          display: flex; flex-direction: column; align-items: center; gap: 4px;
          padding: 10px 4px; border: 2px solid transparent; border-radius: 12px;
          background: var(--bg); cursor: pointer; transition: all 200ms; font-family: inherit;
        }
        .mood-chip:active { transform: scale(0.92); }
        .mood-chip.selected { border-color: var(--primary); background: var(--primary-bg); }
        .selected-mood-display {
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          padding: 14px; background: var(--bg); border-radius: 14px;
          margin-bottom: 14px; cursor: pointer;
        }

        /* Person Chip */
        .person-chip {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--primary-bg); padding: 6px 14px 6px 6px;
          border-radius: 20px; margin-bottom: 12px; font-size: 14px;
        }
        .person-chip button { background: none; border: none; cursor: pointer; color: var(--text-tertiary); padding: 0 4px; font-size: 12px; }
        .person-chip-avatar {
          width: 24px; height: 24px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 11px; font-weight: 600; flex-shrink: 0;
        }

        /* Mention List */
        .mention-list {
          position: absolute; background: var(--surface); border: 1px solid var(--border);
          border-radius: 12px; box-shadow: var(--shadow-lg);
          max-height: 200px; overflow-y: auto; z-index: 10; width: 220px; margin-top: 4px;
        }
        .mention-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; cursor: pointer; transition: background 150ms; font-size: 14px;
        }
        .mention-item:active { background: var(--surface-hover); }

        /* ===== Feed List (Timeline & Cards) ===== */
        .feed-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .timeline-wrap {
          position: relative;
          padding-left: 18px;
          margin-top: 8px;
        }
        .timeline-wrap::before {
          content: '';
          position: absolute;
          top: 10px; bottom: 0; left: 5px;
          width: 2px;
          background: linear-gradient(to bottom, rgba(148,163,184,0.55) 0%, rgba(96,165,250,0.2) 55%, rgba(148,163,184,0.04) 100%);
          border-radius: 2px;
        }
        .timeline-group {
          margin-bottom: 26px;
          position: relative;
        }
        .timeline-date-label {
          font-size: 13px;
          font-weight: 900;
          color: rgba(226,232,240,0.92);
          margin-bottom: 13px;
          display: flex;
          align-items: center;
          position: relative;
          letter-spacing: 0.02em;
        }
        .timeline-dot {
          position: absolute;
          left: -17px;
          width: 10px; height: 10px;
          background: #f8fafc;
          border: 2px solid #020617;
          border-radius: 50%;
          box-shadow: 0 0 0 3px rgba(148,163,184,0.18);
        }
        .timeline-items {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .feed-card {
          position: relative;
          background: #0b1224;
          border-radius: 16px;
          box-shadow: 0 18px 42px rgba(0,0,0,0.26);
          border: 1px solid rgba(148,163,184,0.16);
          animation: cardIn 400ms var(--ease-out) both;
          transition: transform 150ms, border-color 150ms, background 150ms;
          overflow: hidden;
          display: flex;
        }
        .feed-card::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 3px;
          background: rgba(125,211,252,0.5);
        }
        /* Types styling */
        .feed-card.type-note {
          background: linear-gradient(90deg, rgba(59,130,246,0.09), #0b1224 38%);
        }
        .feed-card.type-mood {
          background: linear-gradient(90deg, rgba(139,92,246,0.1), #0b1224 38%);
        }
        .feed-card.type-fitness {
          background: linear-gradient(90deg, rgba(16,185,129,0.1), #0b1224 38%);
        }
        .feed-card.type-tech {
          background: linear-gradient(90deg, rgba(245,158,11,0.1), #0b1224 38%);
        }
        .feed-card.type-grudge {
          background: linear-gradient(90deg, rgba(255,107,107,0.13), rgba(15,23,42,0.78) 42%, rgba(2,6,23,0.58));
        }
        .feed-card.type-gratitude {
          background: linear-gradient(90deg, rgba(0,184,148,0.13), rgba(15,23,42,0.78) 42%, rgba(2,6,23,0.58));
        }
        .feed-card:active { transform: scale(0.992); border-color: rgba(125,211,252,0.2); }
        @keyframes cardIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .feed-card-accent { width: 4px; flex-shrink: 0; }
        .feed-card-inner {
          position: relative;
          z-index: 1;
          flex: 1;
          padding: 17px 18px 15px;
          min-width: 0;
        }
        .feed-card-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 12px; }
        .feed-card-meta { display: flex; align-items: center; gap: 9px; min-width: 0; flex-wrap: wrap; }
        .feed-type-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 5px 10px; border-radius: 7px; font-size: 12px; font-weight: 850;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .feed-person {
          display: flex; align-items: center; gap: 8px;
          background: rgba(2,6,23,0.28); border: 1px solid rgba(148,163,184,0.09);
          cursor: pointer; padding: 4px 8px 4px 4px; font-family: inherit;
          border-radius: 999px;
        }
        .feed-person-avatar {
          width: 22px; height: 22px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 10px; font-weight: 600;
        }
        .feed-person-name { font-size: 12px; font-weight: 750; color: rgba(226,232,240,0.86); }
        .feed-person:active .feed-person-name { color: var(--primary); }
        .feed-time { flex-shrink: 0; font-size: 11px; color: rgba(148,163,184,0.52); font-weight: 800; }
        .feed-card-body {
          font-size: 15px; line-height: 1.75; color: rgba(226,232,240,0.93);
          white-space: pre-wrap; word-break: break-word;
        }
        .feed-card-actions {
          display: flex; gap: 6px; margin-top: 14px; padding-top: 12px;
          border-top: 1px solid rgba(148,163,184,0.08);
        }
        .feed-card-actions button {
          background: rgba(15,23,42,0.4); border: 1px solid rgba(148,163,184,0.08); color: rgba(148,163,184,0.72);
          font-size: 12px; cursor: pointer; padding: 6px 11px;
          border-radius: 7px; transition: all 150ms;
          font-weight: 750; font-family: inherit;
        }
        .feed-card-actions button:active { background: var(--surface-hover); color: var(--text-primary); }
        .feed-card-actions button.danger:active { background: var(--danger-bg); color: var(--danger); }

        /* Empty State */
        .empty-state { text-align: center; padding: 60px 20px; color: var(--text-secondary); }
        .empty-illustration { font-size: 56px; margin-bottom: 16px; }
        .empty-state h3 { margin: 0 0 8px 0; color: var(--text-primary); font-size: 18px; font-weight: 600; }
        .empty-state p { margin: 0; color: var(--text-tertiary); font-size: 14px; }

        @media (max-width: 600px) {
          .diary-feed-root {
            padding: 14px;
            padding-bottom: calc(100px + var(--safe-area-inset-bottom, 0px));
          }
          .df-filter-row {
            display: flex;
            gap: 8px;
          }
          .df-filter-chip {
            flex: 0 0 auto;
            min-width: 76px;
          }
          .feed-card-top {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }
          .feed-time {
            align-self: flex-start;
          }
          .editor-sheet { max-height: 100vh; border-radius: 0; }
        }
      `}</style>
    </div>
    </div>
  );
};

export default DiaryFeed;
